import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import inventoryService, { Inventory } from '../../services/inventoryService';
import retailBillService, { BillItem } from '../../services/retailBillService';

const CreateRetailBill: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Customer details
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');

  // Bill items
  const [items, setItems] = useState<BillItem[]>([]);
  const [inventoryList, setInventoryList] = useState<Inventory[]>([]);
  const [showInventoryModal, setShowInventoryModal] = useState(false);

  // Payment details
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentStatus, setPaymentStatus] = useState('unpaid');
  const [paidAmount, setPaidAmount] = useState(0);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const data = await inventoryService.getAll(1, 1000); // Increased limit to get all inventory
      const filteredInventory = data.data.filter((item) => item.status === 'active' && item.availableQuantity > 0);
      setInventoryList(filteredInventory);
      console.log('Loaded inventory items:', filteredInventory.length);
    } catch (err) {
      console.error('Failed to fetch inventory', err);
      setError('Failed to load inventory. Please refresh the page.');
    }
  };

  const addItemToBill = (inventory: Inventory) => {
    const existingItem = items.find((item) => item.inventoryId === inventory._id);

    if (existingItem) {
      setError('Item already added. Update quantity below.');
      return;
    }

    const newItem: BillItem = {
      inventoryId: inventory._id,
      itemName: inventory.itemName,
      itemCode: inventory.itemCode,
      quantity: 1,
      unit: inventory.unit,
      unitPrice: inventory.unitPrice,
      totalPrice: inventory.unitPrice,
      discount: 0,
      tax: 0,
    };

    setItems([...items, newItem]);
    setShowInventoryModal(false);
    setError('');
  };

  const updateItemQuantity = (index: number, quantity: number) => {
    const updatedItems = [...items];
    updatedItems[index].quantity = quantity;
    updatedItems[index].totalPrice = quantity * updatedItems[index].unitPrice - (updatedItems[index].discount || 0) + (updatedItems[index].tax || 0);
    setItems(updatedItems);
  };

  const updateItemPrice = (index: number, price: number) => {
    const updatedItems = [...items];
    updatedItems[index].unitPrice = price;
    updatedItems[index].totalPrice = updatedItems[index].quantity * price - (updatedItems[index].discount || 0) + (updatedItems[index].tax || 0);
    setItems(updatedItems);
  };

  const updateItemDiscount = (index: number, discount: number) => {
    const updatedItems = [...items];
    updatedItems[index].discount = discount;
    updatedItems[index].totalPrice = updatedItems[index].quantity * updatedItems[index].unitPrice - discount + (updatedItems[index].tax || 0);
    setItems(updatedItems);
  };

  const updateItemTax = (index: number, tax: number) => {
    const updatedItems = [...items];
    updatedItems[index].tax = tax;
    updatedItems[index].totalPrice = updatedItems[index].quantity * updatedItems[index].unitPrice - (updatedItems[index].discount || 0) + tax;
    setItems(updatedItems);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    const totalDiscount = items.reduce((sum, item) => sum + (item.discount || 0), 0);
    const totalTax = items.reduce((sum, item) => sum + (item.tax || 0), 0);
    const grandTotal = subtotal - totalDiscount + totalTax;

    return { subtotal, totalDiscount, totalTax, grandTotal };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!customerName || !customerPhone) {
      setError('Customer name and phone are required');
      return;
    }

    if (items.length === 0) {
      setError('Please add at least one item to the bill');
      return;
    }

    const { subtotal, totalDiscount, totalTax, grandTotal } = calculateTotals();

    setLoading(true);

    try {
      await retailBillService.create({
        customerName,
        customerPhone,
        customerEmail: customerEmail || undefined,
        customerAddress: customerAddress || undefined,
        items,
        subtotal,
        totalDiscount,
        totalTax,
        grandTotal,
        paymentMethod: paymentMethod as any,
        paymentStatus: paymentStatus as any,
        paidAmount: paymentStatus === 'paid' ? grandTotal : paidAmount,
        notes: notes || undefined,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/retail-bills');
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to create bill';
      setError(errorMessage);

      // If inventory not found error, reload inventory
      if (errorMessage.includes('not found for this company')) {
        console.error('Inventory mismatch detected. Reloading inventory...');
        fetchInventory();
        setError(errorMessage + ' - Your inventory list has been refreshed. Please select items again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, totalDiscount, totalTax, grandTotal } = calculateTotals();

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/retail-bills')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Bills
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create Retail Bill</h1>
        <p className="mt-1 text-sm text-gray-600">
          Generate a new bill for retail customers
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600">
              ✓ Bill created successfully! Redirecting...
            </p>
          </div>
        )}

        {/* Customer Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Name *
              </label>
              <input
                type="text"
                required
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                required
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="+91 9876543210"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email (Optional)
              </label>
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="customer@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address (Optional)
              </label>
              <input
                type="text"
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
                placeholder="123 Main St, City"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Bill Items */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Bill Items</h3>
            <button
              type="button"
              onClick={() => setShowInventoryModal(true)}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Item
            </button>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <ShoppingCartIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No items added yet. Click "Add Item" to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price (₹)</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Discount (₹)</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tax (₹)</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total (₹)</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {item.itemName}
                        <br />
                        <span className="text-xs text-gray-500">{item.itemCode}</span>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItemQuantity(index, Number(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) => updateItemPrice(index, Number(e.target.value))}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.discount || 0}
                          onChange={(e) => updateItemDiscount(index, Number(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.tax || 0}
                          onChange={(e) => updateItemTax(index, Number(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                        ₹{item.totalPrice.toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Totals */}
          {items.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <div className="flex justify-end space-y-2">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">₹{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Discount:</span>
                    <span className="font-medium text-red-600">-₹{totalDiscount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Tax:</span>
                    <span className="font-medium">₹{totalTax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Grand Total:</span>
                    <span className="text-indigo-600">₹{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="upi">UPI</option>
                <option value="net_banking">Net Banking</option>
                <option value="wallet">Wallet</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Status
              </label>
              <select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
              </select>
            </div>

            {paymentStatus === 'partial' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paid Amount (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  max={grandTotal}
                  step="0.01"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Additional notes or remarks"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/retail-bills')}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || items.length === 0}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Bill...' : 'Create Bill'}
          </button>
        </div>
      </form>

      {/* Inventory Selection Modal */}
      {showInventoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Select Item from Inventory</h3>
              <button
                onClick={() => setShowInventoryModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Info notice */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                ℹ️ Showing only your company's inventory items ({inventoryList.length} items available)
              </p>
            </div>

            <div className="space-y-2">
              {inventoryList.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-2">No inventory items available</p>
                  <button
                    onClick={fetchInventory}
                    type="button"
                    className="text-indigo-600 hover:text-indigo-700 text-sm"
                  >
                    Reload Inventory
                  </button>
                </div>
              ) : (
                inventoryList.map((item) => (
                  <div
                    key={item._id}
                    onClick={() => addItemToBill(item)}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 cursor-pointer transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">{item.itemName}</h4>
                        <p className="text-sm text-gray-500">Code: {item.itemCode}</p>
                        <p className="text-sm text-gray-500">Available: {item.availableQuantity} {item.unit}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-indigo-600">₹{item.unitPrice.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">per {item.unit}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateRetailBill;
